import { useAtomValue, useSetAtom } from "jotai";
import { loadable } from "jotai/utils";
import { useCallback, useRef } from "react";

import {
	$runWorkflow,
	$workflowRuns,
	$workflowSchedule,
	$workflows,
	type Workflow,
	type WorkflowRun,
	type WorkflowScheduleEntry,
} from "../../internal/workflows";

// Stable loadable atoms — hoisted to module level so useAtomValue reuses the
// same atom across renders instead of subscribing to a new one each time.
const $workflowsLoadable = loadable($workflows);
const $workflowRunsLoadable = loadable($workflowRuns);
const $workflowScheduleLoadable = loadable($workflowSchedule);

export const useWorkflows = () => {
	const workflowsLoadable = useAtomValue($workflowsLoadable);
	const workflowRunsLoadable = useAtomValue($workflowRunsLoadable);
	const scheduleLoadable = useAtomValue($workflowScheduleLoadable);
	const setWorkflows = useSetAtom($workflows);
	const runWorkflow = useSetAtom($runWorkflow);

	// Keep previous data while reloading to avoid layout flicker
	const prevWorkflows = useRef<Workflow[]>([]);
	const prevRuns = useRef<WorkflowRun[]>([]);
	const prevSchedule = useRef<WorkflowScheduleEntry[]>([]);

	const workflows =
		workflowsLoadable.state === "hasData" ? workflowsLoadable.data : prevWorkflows.current;
	const runs =
		workflowRunsLoadable.state === "hasData" ? workflowRunsLoadable.data : prevRuns.current;
	const schedule =
		scheduleLoadable.state === "hasData" ? scheduleLoadable.data : prevSchedule.current;

	prevWorkflows.current = workflows;
	prevRuns.current = runs;
	prevSchedule.current = schedule;

	const upsertWorkflow = useCallback(
		async (workflow: Workflow) => {
			await setWorkflows(workflow);
		},
		[setWorkflows]
	);

	const deleteWorkflow = useCallback(
		async (id: string) => {
			await setWorkflows({ delete: id });
		},
		[setWorkflows]
	);

	const triggerWorkflow = useCallback(
		async (workflow: Workflow) => {
			await runWorkflow(workflow);
		},
		[runWorkflow]
	);

	return {
		workflows,
		runs,
		schedule,
		isLoading: workflowsLoadable.state === "loading",
		upsertWorkflow,
		deleteWorkflow,
		triggerWorkflow,
	};
};
